import {
  MUIDateTimePicker,
  MUISelect,
  MUITextField,
  TextLink,
} from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import dayjs, { Dayjs } from "dayjs"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useState } from "react"
import {
  Controller,
  ControllerRenderProps,
  Resolver,
  useForm,
  useWatch,
} from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { useApplicationRoles } from "../../../../hooks/useApplicationRoles"
import { useApplications } from "../../../../hooks/useApplications"
import { useNotificationTopics } from "../../../../hooks/useNotificationTopics"
import { digitalManagerApi } from "../../../../services/api"
import DiMaCheckboxList from "../../../components/DiMaCheckboxList"
import { DiMaProgress } from "../../../components/DiMaProgress"
import { ControllerRendererProps } from "../../../types/reactHookForm"
import { FieldRow, FormPage } from "../../../components/FormPage"
import { createNotificationValidationSchema } from "../../../components/Helpers"
import { AccessLevel, DimaFeatureShortNameEnum } from "../../../models/Authorization"
import { Guid } from "../../../models/CustomType"
import { NotificationPost } from "../../../models/Notification"
import { NotificationTargetType } from "../../../models/NotificationTarget"
import { getNowDateTime, getUtcDateTime } from "../../../utils/getFormattedDate"

type NotificationPostTextFields =
  | "title"
  | "body"
  | "url"
  | "applicationVersion"
  | "operatingSystem"

const validationSchema = createNotificationValidationSchema({
  includeApplicationId: true,
})

const MAX_DATE = dayjs("9999-12-31T23:59:59")

const returnURL = `/notifications`
const CANNOT_CREATE_NOTIFICATION_MESSAGE = "Cannot create notification"

export function NotificationCreate() {
  const { applicationsList } = useApplications(
    DimaFeatureShortNameEnum.NM,
    AccessLevel.Write
  )
  const navigate = useNavigate()
  const [checkedApplicationRolesIds, setCheckedApplicationRolesIds] = useState<Guid[]>([])

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<NotificationPost & { targetType: NotificationTargetType }>({
    resolver: zodResolver(validationSchema) as Resolver<
      NotificationPost & { targetType: NotificationTargetType }
    >,
    mode: "onChange",
  })

  const targetType = useWatch({
    control,
    name: "targetType",
  })

  useEffect(() => {
    reset({
      applicationId: "",
      topicId: "",
      title: "",
      body: "",
      bodyHtml: "",
      bodyPlate: "",
      url: "",
      applicationVersion: "",
      operatingSystem: "",
      validityStartDate: new Date(getNowDateTime()).toISOString(),
      validityEndDate: "",
      isAnonymous: false,
    })
  }, [reset])

  //#region OnSubmit
  const validateAndPrepareNotification = (
    values: NotificationPost & { targetType: NotificationTargetType }
  ) => {
    let errorInTargets = false
    let isAnonymous = false

    if (
      values.targetType === NotificationTargetType.Role &&
      checkedApplicationRolesIds.length === 0
    ) {
      toast.warning("Select at least one role")
      errorInTargets = true
    }

    const utcValidityStartDate = values.validityStartDate
      ? getUtcDateTime(values.validityStartDate)
      : ""
    const utcValidityEndDate = values.validityEndDate
      ? getUtcDateTime(values.validityEndDate)
      : ""

    if (values.targetType === NotificationTargetType.Anonymous) {
      values.targetType = NotificationTargetType.All
      isAnonymous = true
    }

    const notification: NotificationPost = {
      applicationId: values.applicationId,
      topicId: values.topicId,
      title: values.title,
      body: values.body,
      bodyHtml: `<p>${values.body.replaceAll("\n", "<br>")}</p>`,
      bodyPlate: values.body,
      url: values.url ? values.url : null,
      applicationVersion: values.applicationVersion ? values.applicationVersion : null,
      operatingSystem: values.operatingSystem ? values.operatingSystem : null,
      validityStartDate: utcValidityStartDate,
      validityEndDate: utcValidityEndDate,
      isAnonymous,
    }

    return { errorInTargets, notification, targetType: values.targetType }
  }

  const createNotification = async (
    notification: NotificationPost,
    createTargetType: NotificationTargetType
  ) => {
    try {
      const res = await digitalManagerApi.post(
        `/notifications/applications`,
        notification
      )

      if (res.status !== StatusCodes.CREATED || !res.data?.id) {
        toast.error(CANNOT_CREATE_NOTIFICATION_MESSAGE)
        return
      }

      const notificationId = res.data.id
      const promises = []

      if (createTargetType === NotificationTargetType.All) {
        promises.push(
          digitalManagerApi.post(`/notifications/${notificationId}/targets`, {
            type: NotificationTargetType.All,
          })
        )
      } else {
        checkedApplicationRolesIds.forEach((role) => {
          promises.push(
            digitalManagerApi.post(`/notifications/${notificationId}/targets`, {
              type: NotificationTargetType.Role,
              value: role,
            })
          )
        })
      }

      try {
        await Promise.all(promises)
        toast.success("Notification created")
        navigate(returnURL)
      } catch {
        toast.error(CANNOT_CREATE_NOTIFICATION_MESSAGE)
        await digitalManagerApi.delete(`/notifications/${notificationId}`)
      }
    } catch {
      toast.error(CANNOT_CREATE_NOTIFICATION_MESSAGE)
    }
  }

  const onSubmit = async (
    values: NotificationPost & { targetType: NotificationTargetType }
  ) => {
    const {
      errorInTargets,
      notification,
      targetType: newTargetType,
    } = validateAndPrepareNotification(values)

    if (errorInTargets) {
      return
    }

    await createNotification(notification, newTargetType)
  }
  //#endregion

  const { notificationTopicsList, isLoadingNotificationTopics } = useNotificationTopics(
    false,
    getValues("applicationId")
  )
  const { applicationRoles, isLoadingApplicationRoles } = useApplicationRoles(
    getValues("applicationId")
  )

  //#region Application
  const handleSelectChangeWithTopicReset = useCallback(
    (
      field: ControllerRenderProps<
        NotificationPost & { targetType: NotificationTargetType },
        "applicationId"
      >
    ) =>
      (value: string) => {
        field.onChange(value)
        setValue("topicId", "")
      },
    [setValue]
  )

  const handleApplicationSelectChange = useCallback(
    (
      field: ControllerRenderProps<
        NotificationPost & { targetType: NotificationTargetType },
        "applicationId"
      >
    ) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        handleSelectChangeWithTopicReset(field)(value)
      },
    [handleSelectChangeWithTopicReset]
  )

  const handleTextFieldChange = useCallback(
    (
      field: ControllerRenderProps<
        NotificationPost & { targetType: NotificationTargetType },
        NotificationPostTextFields
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderApplicationSelect = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      NotificationPost & { targetType: NotificationTargetType },
      "applicationId"
    >) => (
      <MUISelect
        name={field.name}
        id="applicationId"
        size={32}
        dropdownLOV={applicationsList.map((a) => ({
          id: String(a.id),
          name: a.name ?? "",
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleApplicationSelectChange(field)}
        onBlur={field.onBlur}
      />
    ),
    [applicationsList, handleApplicationSelectChange]
  )
  //#endregion

  //#region Title
  const renderTextFieldTitle = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      NotificationPost & { targetType: NotificationTargetType },
      "title"
    >) => (
      <MUITextField
        id="title"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.title?.message === "string"
            ? { title: { message: errors.title.message } }
            : undefined
        }
      />
    ),
    [errors.title, handleTextFieldChange]
  )
  //#endregion

  //#region Body
  const renderTextFieldBody = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      NotificationPost & { targetType: NotificationTargetType },
      "body"
    >) => (
      <MUITextField
        id="body"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        multiline
        errors={
          typeof errors.body?.message === "string"
            ? { body: { message: errors.body.message } }
            : undefined
        }
      />
    ),
    [errors.body, handleTextFieldChange]
  )
  //#endregion

  //#region Url
  const renderTextFieldUrl = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      NotificationPost & { targetType: NotificationTargetType },
      "url"
    >) => (
      <MUITextField
        id="url"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.url?.message === "string"
            ? { url: { message: errors.url.message } }
            : undefined
        }
      />
    ),
    [errors.url, handleTextFieldChange]
  )
  //#endregion

  //#region Application Versions
  const renderTextFieldApplicationVersion = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      NotificationPost & { targetType: NotificationTargetType },
      "applicationVersion"
    >) => (
      <MUITextField
        id="applicationVersion"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
      />
    ),
    [handleTextFieldChange]
  )
  //#endregion

  //#region Operating Systems
  const renderTextFieldOperatingSystem = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      NotificationPost & { targetType: NotificationTargetType },
      "operatingSystem"
    >) => (
      <MUITextField
        id="operatingSystem"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
      />
    ),
    [handleTextFieldChange]
  )
  //#endregion

  //#region Validity Start Date

  const handleValidityStartDateChange = useCallback(
    (date: Dayjs | null) => {
      setValue("validityStartDate", date ? date.toISOString() : "", {
        shouldValidate: true,
      })
    },
    [setValue]
  )

  const handleValidityStartDatePickerChange = useCallback(
    (date: Dayjs | null) => {
      if (date?.isAfter(MAX_DATE)) {
        setValue("validityStartDate", MAX_DATE.toISOString(), {
          shouldValidate: true,
        })
        return
      }
      handleValidityStartDateChange(date)
    },
    [handleValidityStartDateChange, setValue]
  )

  const renderDateTimePickerValidityStartDate = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      NotificationPost & { targetType: NotificationTargetType },
      "validityStartDate"
    >) => (
      <MUIDateTimePicker
        id="validityStartDate"
        name="validityStartDate"
        size={40}
        value={field.value ? dayjs(field.value) : null}
        isDisabled={false}
        onChange={handleValidityStartDatePickerChange}
        errors={
          errors.validityStartDate
            ? { validityStartDate: { message: String(errors.validityStartDate.message) } }
            : undefined
        }
      />
    ),
    [errors.validityStartDate, handleValidityStartDatePickerChange]
  )
  //#endregion

  //#region Validity End Date
  const handleValidityEndDateChange = useCallback(
    (date: Dayjs | null) => {
      setValue("validityEndDate", date ? date.toISOString() : "", {
        shouldValidate: true,
      })
    },
    [setValue]
  )

  const handleValidityEndDatePickerChange = useCallback(
    (date: Dayjs | null) => {
      const startDateValue = getValues("validityStartDate")
        ? dayjs(getValues("validityStartDate"))
        : null

      if (date && startDateValue && date.isBefore(startDateValue)) {
        setValue("validityEndDate", "", { shouldValidate: true })
        return
      }

      if (date?.isAfter(MAX_DATE)) {
        setValue("validityEndDate", MAX_DATE.toISOString(), { shouldValidate: true })
        return
      }

      handleValidityEndDateChange(date)
    },
    [getValues, handleValidityEndDateChange, setValue]
  )

  const renderDateTimePickerValidityEndDate = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      NotificationPost & { targetType: NotificationTargetType },
      "validityEndDate"
    >) => (
      <MUIDateTimePicker
        id="validityEndDate"
        name="validityEndDate"
        size={32}
        value={field.value ? dayjs(field.value) : null}
        isDisabled={false}
        onChange={handleValidityEndDatePickerChange}
        errors={
          errors.validityEndDate
            ? { validityEndDate: { message: String(errors.validityEndDate.message) } }
            : undefined
        }
      />
    ),
    [errors.validityEndDate, handleValidityEndDatePickerChange]
  )
  //#endregion

  const handleTopicSelectChange = useCallback(
    (
      field: ControllerRenderProps<
        NotificationPost & { targetType: NotificationTargetType },
        "topicId"
      >
    ) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        field.onChange(value)
      },
    []
  )

  //#region Topic
  const renderSelectTopicId = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      NotificationPost & { targetType: NotificationTargetType },
      "topicId"
    >) => (
      <MUISelect
        name={field.name}
        id="topicId"
        size={32}
        dropdownLOV={notificationTopicsList.map((t) => ({
          id: String(t.id),
          name: t.name ?? "",
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleTopicSelectChange(field)}
        onBlur={field.onBlur}
      />
    ),
    [handleTopicSelectChange, notificationTopicsList]
  )
  //#endregion

  //#region Who will receive
  const handleSelectChangeWithRoleCheck = useCallback(
    (
      field: ControllerRenderProps<
        NotificationPost & { targetType: NotificationTargetType },
        "targetType"
      >
    ) =>
      (value: NotificationTargetType) => {
        field.onChange(value)
        if (value !== NotificationTargetType.Role) {
          setCheckedApplicationRolesIds([])
        }
      },
    [setCheckedApplicationRolesIds]
  )

  const handleNotificationTargetSelectChange = useCallback(
    (
      field: ControllerRenderProps<
        NotificationPost & { targetType: NotificationTargetType },
        "targetType"
      >
    ) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        handleSelectChangeWithRoleCheck(field)(value as NotificationTargetType)
      },
    [handleSelectChangeWithRoleCheck]
  )

  const renderNotificationTargetSelect = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      NotificationPost & { targetType: NotificationTargetType },
      "targetType"
    >) => (
      <MUISelect
        name={field.name}
        size={32}
        dropdownLOV={[
          { id: String(NotificationTargetType.All), name: "All logged users" },
          {
            id: String(NotificationTargetType.Role),
            name: "Users with specific roles",
            isDisabled: isLoadingApplicationRoles || applicationRoles.length === 0,
          },
          { id: String(NotificationTargetType.Anonymous), name: "Only NOT logged users" },
        ]}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleNotificationTargetSelectChange(field)}
        onBlur={field.onBlur}
      />
    ),
    [
      applicationRoles.length,
      handleNotificationTargetSelectChange,
      isLoadingApplicationRoles,
    ]
  )
  //#endregion

  const handleSetCheckedApplicationRolesIds = useCallback(
    (update: React.SetStateAction<Guid[]>) =>
      setCheckedApplicationRolesIds((prev) =>
        typeof update === "function" ? update(prev) : update
      ),
    []
  )

  const handleCreateTopicLinkClick = useCallback(
    () => navigate("/notifications#topics"),
    [navigate]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={
        isDirty &&
        !(
          targetType === NotificationTargetType.Role &&
          checkedApplicationRolesIds.length === 0
        )
      }
      isValid={isValid}
    >
      <FieldRow label="Application">
        <>
          <Controller
            name="applicationId"
            control={control}
            render={renderApplicationSelect}
          />
          {errors.applicationId && (
            <MUITextField
              isDisabled
              value=""
              errors={{
                applicationId: {
                  message:
                    typeof errors.applicationId?.message === "string"
                      ? errors.applicationId.message
                      : "",
                },
              }}
            />
          )}
        </>
      </FieldRow>
      <FieldRow label="Title">
        <Controller name="title" control={control} render={renderTextFieldTitle} />
      </FieldRow>
      <FieldRow label="Body">
        <Controller name="body" control={control} render={renderTextFieldBody} />
      </FieldRow>
      <FieldRow label="Url">
        <Controller name="url" control={control} render={renderTextFieldUrl} />
      </FieldRow>
      <FieldRow label="Application Versions">
        <Controller
          name="applicationVersion"
          control={control}
          render={renderTextFieldApplicationVersion}
        />
      </FieldRow>
      <FieldRow label="Operating Systems">
        <Controller
          name="operatingSystem"
          control={control}
          render={renderTextFieldOperatingSystem}
        />
      </FieldRow>
      <FieldRow label="Validity Start Date">
        <Controller
          name="validityStartDate"
          control={control}
          rules={{ required: "Validity Start Date is required" }}
          render={renderDateTimePickerValidityStartDate}
        />
      </FieldRow>
      <FieldRow label="Validity End Date">
        <Controller
          name="validityEndDate"
          control={control}
          render={renderDateTimePickerValidityEndDate}
        />
      </FieldRow>
      <FieldRow label="Topic">
        <>
          <Controller name="topicId" control={control} render={renderSelectTopicId} />
          {errors.topicId && (
            <MUITextField
              isDisabled
              value=""
              errors={{
                topicId: {
                  message:
                    typeof errors.topicId?.message === "string"
                      ? errors.topicId.message
                      : "",
                },
              }}
            />
          )}
          {!isLoadingNotificationTopics && notificationTopicsList.length === 0 && (
            <TextLink
              leadingIconName="icon-abb_data-connection_edit icon_abb_dias"
              onClick={handleCreateTopicLinkClick}
              size={14}
              text="Please, create a notification topic"
            />
          )}
        </>
      </FieldRow>
      <FieldRow label="Who will receive">
        <Controller
          name="targetType"
          control={control}
          render={renderNotificationTargetSelect}
        />
      </FieldRow>
      {targetType === NotificationTargetType.Role && (
        <>
          {!applicationRoles && <DiMaProgress />}
          {applicationRoles && (
            <DiMaCheckboxList
              items={applicationRoles.map((role) => ({
                id: role.id,
                title: role.name,
                description: role.description,
              }))}
              checkedItemsIds={checkedApplicationRolesIds}
              setCheckedItemsIds={handleSetCheckedApplicationRolesIds}
            />
          )}
        </>
      )}
    </FormPage>
  )
}
